import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useProducts } from "../../admin/products/useProducts";
import { useCalculationProductAlternatives } from "../../calculations/alternativeProducts/useCalculationProductAlternatives";
import type { CalculationProduct } from "../../calculations/calculationTypes";

import ProductDetails from "../../components/productDetails/ProductDetails";
import type { Currency } from "../../data/BaseEntity";
import { formatDateWithDay } from "../generic/formatDateWithDay";

interface Props {
  selectedProduct: CalculationProduct;
  calculationCurrency: Currency;
  calculationMargin: number;
  participantsNumber: number;
  onClose(): void;
  onProductReplace(productId: string, replacement: CalculationProduct): void;
}

export function AlternativesProductsDialog({
  selectedProduct,
  calculationCurrency,
  calculationMargin,
  participantsNumber,
  onClose,
  onProductReplace,
}: Props) {
  const { products } = useProducts();
  const { alternatives, selectedProductDoubleTwinPrice } = useCalculationProductAlternatives(
    selectedProduct,
    calculationCurrency,
    calculationMargin,
    products,
    participantsNumber,
  );

  const selectedProductName = products.find((p) => p.id === selectedProduct.id)?.name ?? "";

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: "bold" }}>Dostępne są alternatywne produkty</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="primary" gutterBottom>
            Obecnie wybrany produkt:
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            {selectedProductName}
          </Typography>
          {selectedProductDoubleTwinPrice && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Cena pokoju Double/Twin w dniu {formatDateWithDay(selectedProduct.date)}:
              </Typography>
              <Typography variant="body1" color="primary.dark" sx={{ fontWeight: "bold" }}>
                {selectedProductDoubleTwinPrice.toDisplayValue(calculationCurrency)}
              </Typography>
            </Box>
          )}
        </Box>

        <Alert
          icon={<InfoOutlinedIcon />}
          severity="info"
          sx={{ mb: 3, bgcolor: "rgba(13, 110, 253, 0.04)", border: "1px solid rgba(13, 110, 253, 0.1)" }}
        >
          <AlertTitle sx={{ fontWeight: "bold", fontSize: "0.9rem" }}>Standard Porównania</AlertTitle>
          <Typography variant="caption" sx={{ display: "block", lineHeight: 1.5 }}>
            Poniższe zestawienie bazuje na <strong>podstawowym produkcie (pokój 2-osobowy)</strong> dla każdego obiektu.
            Pozwala to na rzetelne porównanie standardu cenowego obiektów (tzv. &quot;apples-to-apples&quot;) bez
            uwzględniania dodatkowych usług wybranych w Twojej bieżącej kalkulacji (np. śniadanie, parking, wyższe
            standardy pokoju).
          </Typography>
        </Alert>

        <TableContainer sx={{ border: "1px solid rgba(0,0,0,0.05)", borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Produkt alternatywny</TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "text.secondary", textAlign: "center" }}>
                  Szczegóły
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "text.secondary", textAlign: "right" }}>
                  Cena (DOUBLE/TWIN)
                </TableCell>
                <TableCell sx={{ width: 120 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {alternatives.map((alt) => {
                const altProduct = products.find((p) => p.id === alt.product.id);
                return (
                  <TableRow key={alt.product.id} hover>
                    <TableCell>{altProduct?.name ?? alt.product.id}</TableCell>
                    <TableCell align="center">
                      <ProductDetails product={altProduct} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      {alt.doubleTwinPrice.toDisplayValue(calculationCurrency)}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        sx={{ color: "primary.main", bgcolor: "rgba(13, 110, 253, 0.04)" }}
                        onClick={() => {
                          onClose();
                          return onProductReplace(selectedProduct.id, alt.product);
                        }}
                      >
                        <SwapHorizIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {alternatives.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ color: "text.secondary", py: 3 }}>
                    Brak alternatywnych produktów dla wybranego terminu
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" color="secondary" onClick={onClose} sx={{ px: 4 }}>
          ZAMKNIJ
        </Button>
      </DialogActions>
    </Dialog>
  );
}
